// useLogicValidation.ts
import { useCallback } from 'react';
import { Edge, Node } from '@xyflow/react';
import { useIntl } from 'react-intl';

import {
  AllFlowNodes,
  nodeTypeComponents,
  SensorButtonNodeType,
  SensorTrihowSpecificNodeType,
} from '../Nodes/types';
import { GeneralSettingsType } from '../SettingsSidebar/types';
import { findNodeConnection } from '../util/findNodeConnectionLogic';
import { findLanguageAnyNodes } from '../util/findLanguageAnyNodes';

type InvalidSettingsReport = {
  controllers: boolean;
  nfcReaders: boolean;
  buttons: boolean;
  buzzers: boolean;
  leds: boolean;
  screens: boolean;
};

// Type to either show specific error message or fallback to a default message
export type InvalidNodeResponse = string | boolean;

type NodeTypeKeys = keyof typeof nodeTypeComponents;

// Required fields for each node type
export const requiredFieldsByNodeType: Partial<Record<NodeTypeKeys, string[]>> =
  {
    'sensor-nfcSpecific': ['trihow'],
    'sensor-button': ['buttons'],
    'sensor-buzzer': ['buzzers'],
    'action-switchLanguage': ['targetLanguage'],
    'action-toggleLED': ['leds'],
    'action-turnOnLED': ['leds'],
    'action-turnOffLED': ['leds'],
    'action-dispatchCustomEvent': ['eventName', 'eventData'],
    'action-customCode': ['customCode'],
  };

// Config object for validation
const VALIDATION_RULES = {
  TOUCH_SENSOR: {
    MAX_WITHOUT_SWITCH_LANGUAGE: 1,
  },
  SHOW_SINGLE_SCREEN: {
    MIN_BUTTONS_FOR_GENERIC_KEY: 2,
    MIN_TRIHOWS_FOR_GENERIC_KEY: 2,
  },
} as const;

// Helper to get the count of a property from a connected node of a given type
export function getConnectedNodeCount<T extends Node>(
  node: Node,
  nodes: Node[],
  edges: Edge[],
  sourceNodeType: string,
  property: string,
): number {
  const connection = findNodeConnection({
    selectedNode: node as AllFlowNodes,
    sourceNodeType,
    nodes,
    edges,
  });
  if (
    connection &&
    typeof connection === 'object' &&
    'sourceNodeId' in connection
  ) {
    const connectedNode = nodes.find(
      (n) => n.id === connection.sourceNodeId && n.type === sourceNodeType,
    ) as T | undefined;
    const arr = (connectedNode?.data?.options as any)?.[property];
    return Array.isArray(arr) ? arr.length : 0;
  }
  return 0;
}

// Helper to check if generic key is required for showSingleScreen
export function requiresGenericKey(
  node: Node,
  nodes: Node[],
  edges: Edge[],
): boolean {
  const connectedButtonCount = getConnectedNodeCount<SensorButtonNodeType>(
    node,
    nodes,
    edges,
    'sensor-button',
    'buttons',
  );
  const connectedSpecificTrihowCount =
    getConnectedNodeCount<SensorTrihowSpecificNodeType>(
      node,
      nodes,
      edges,
      'sensor-nfcSpecific',
      'trihow',
    );

  return (
    !!findNodeConnection({
      selectedNode: node as AllFlowNodes,
      sourceNodeType: 'sensor-buzzer',
      nodes,
      edges,
    }) ||
    !!findNodeConnection({
      selectedNode: node as AllFlowNodes,
      sourceNodeType: 'sensor-touch',
      nodes,
      edges,
    }) ||
    connectedButtonCount >=
      VALIDATION_RULES.SHOW_SINGLE_SCREEN.MIN_BUTTONS_FOR_GENERIC_KEY ||
    connectedSpecificTrihowCount >=
      VALIDATION_RULES.SHOW_SINGLE_SCREEN.MIN_TRIHOWS_FOR_GENERIC_KEY
  );
}

export function useLogicValidation() {
  const intl = useIntl();

  const validateNodeOptions = useCallback(
    (node: Node, nodes: Node[], edges: Edge[]): InvalidNodeResponse => {
      const options = (node.data as any)?.options ?? {};

      // Get all language-any sensors in the current nodes
      const {
        languageAnyTrihows,
        languageAnyButtons,
        languageAnyBuzzers,
        languageAnyTouches,
      } = findLanguageAnyNodes(nodes);

      // Node-specific validators
      // eslint-disable-next-line no-fallthrough
      switch (node.type) {
        case 'action-dispatchCustomEvent': {
          if (!options.eventName) return false;
          try {
            JSON.parse(options.eventData);
          } catch (error) {
            console.warn(
              `Invalid JSON in eventData for node ${node.id}:`,
              error,
            );
            return intl.formatMessage({
              defaultMessage: 'Invalid JSON format in eventData field.',
              id: 't+Ybbl',
            });
          }
          break;
        }

        case 'action-showSingleScreen': {
          const requireGenericKey = requiresGenericKey(node, nodes, edges);
          if (requireGenericKey) {
            const key = options.genericKey;
            if (key === null || key.toString().trim() === '') return false;
          } else {
            options.genericKey = null;
          }
          break;
        }

        case 'sensor-touch': {
          const touchSensors = nodes.filter((n) => n.type === 'sensor-touch');
          const hasSwitchLanguage = nodes.some(
            (n) => n.type === 'action-switchLanguage',
          );
          if (
            touchSensors.length >
              VALIDATION_RULES.TOUCH_SENSOR.MAX_WITHOUT_SWITCH_LANGUAGE &&
            !hasSwitchLanguage
          ) {
            return intl.formatMessage({
              defaultMessage:
                'No more than 2 sensor-touch nodes are allowed when language selective is turned off.',
              id: '29Z36X',
            });
          }
          if (languageAnyTouches.length > 1) {
            return intl.formatMessage({
              defaultMessage:
                'No more than 1 sensor-touch node with "works in language" set to "Any" is allowed.',
              id: 'ifEs90',
            });
          }
          break;
        }

        case 'sensor-nfcSpecific': {
          if (languageAnyTrihows.length > 1) {
            const trihowSignatures = languageAnyTrihows.map((b) =>
              JSON.stringify(b.data.options.trihow ?? []),
            );
            const uniqueSignatures = new Set(trihowSignatures);
            if (uniqueSignatures.size < languageAnyTrihows.length) {
              return intl.formatMessage({
                defaultMessage:
                  'No more than 1 sensor-trihow node with "works in language" set to "Any" is allowed.',
                id: 'a2QqfQ',
              });
            }
          }
          // fallthrough - intentionally check required fields if validation passes
        }

        case 'sensor-button': {
          if (languageAnyButtons.length > 1) {
            const buttonsSignatures = languageAnyButtons.map((b) =>
              JSON.stringify(b.data.options.buttons ?? []),
            );
            const uniqueSignatures = new Set(buttonsSignatures);
            if (uniqueSignatures.size < languageAnyButtons.length) {
              return intl.formatMessage({
                defaultMessage:
                  'No more than 1 sensor-button node with "works in language" set to "Any" is allowed.',
                id: 'Lxrd95',
              });
            }
          }
          // fallthrough - intentionally check required fields if validation passes
        }

        case 'sensor-buzzer': {
          if (languageAnyBuzzers.length > 1) {
            const buzzerSignatures = languageAnyBuzzers.map((b) =>
              JSON.stringify(b.data.options.buzzers ?? []),
            );
            const uniqueSignatures = new Set(buzzerSignatures);
            if (uniqueSignatures.size < languageAnyBuzzers.length) {
              return intl.formatMessage({
                defaultMessage:
                  'No more than 1 sensor-buzzer node with "works in language" set to "Any" is allowed.',
                id: '3bVZ6v',
              });
            }
          }
          // fallthrough - intentionally check required fields if validation passes
        }

        default: {
          const requiredFields =
            requiredFieldsByNodeType[node.type as NodeTypeKeys];
          if (requiredFields) {
            for (const field of requiredFields) {
              const value = options[field];
              if (value == null || value.toString().trim() === '') {
                return false;
              }
            }
          }
          break;
        }
      }

      return true;
    },
    [intl],
  );

  const validateSettings = useCallback(
    (settings: GeneralSettingsType): InvalidSettingsReport => {
      const isEmpty = (val: any) =>
        val === null || val === undefined || val.toString().trim() === '';

      return {
        controllers: settings.controllers.some(
          (c) => isEmpty(c.type) || isEmpty(c.serialnumber),
        ),
        nfcReaders: settings.nfcReaders.some(
          (r) => isEmpty(r.type) || isEmpty(r.channel),
        ),
        buttons: settings.buttons.some(
          (b) =>
            isEmpty(b.channel) ||
            b.buttonIdentifier === null ||
            isEmpty(b.name),
        ),
        buzzers: settings.buzzers.some(
          (b) => isEmpty(b.channel) || isEmpty(b.name),
        ),
        leds: settings.leds.some(
          (l) =>
            isEmpty(l.channel) || l.ledIdentifier === null || isEmpty(l.name),
        ),
        screens: settings.screens.some(
          (s) => s.screenNr === null || isEmpty(s.name),
        ),
      };
    },
    [],
  );

  const validateSensorToActionConnectivity = useCallback(
    (nodes: Node[], edges: Edge[]): string | null => {
      const sensors = nodes.filter((node) => node.type?.startsWith('sensor-'));
      const actions = nodes.filter((node) => node.type?.startsWith('action-'));

      if (!actions.length) return null;
      if (!sensors.length) return actions[0].id;

      const graph: Record<string, string[]> = {};
      for (const edge of edges) {
        if (!graph[edge.source]) graph[edge.source] = [];
        graph[edge.source].push(edge.target);
      }

      const reachable = new Set<string>();
      const visit = (nodeId: string, visited: Set<string>) => {
        if (visited.has(nodeId)) return;
        visited.add(nodeId);
        reachable.add(nodeId);
        (graph[nodeId] || []).forEach((next) => visit(next, visited));
      };

      sensors.forEach((s) => visit(s.id, new Set()));

      return actions.find((a) => !reachable.has(a.id))?.id ?? null;
    },
    [],
  );

  return {
    validateNodeOptions,
    validateSettings,
    validateSensorToActionConnectivity,
  };
}
